<script setup lang="ts">
import { alignments, resolveMods, skillStats, emitRest, type StatblockData } from '../statblock/statblock'
import { ensurePrefix, titleCase, arrayify } from '../util'
import Statistic from '../statblock/Statistic.vue'
import AbilityScores from '../statblock/AbilityScores.vue'
import Spellcasting from '../statblock/Spellcasting.vue'
import StatblockAttributes from '../statblock/StatblockAttributes.vue'

const props = defineProps<{
  stats?: StatblockData
}>()

const doc = useDocument()

const stats = computed<StatblockData>(() => {
  if (props.stats) return props.stats
  return doc.value as unknown as StatblockData
})

const mods = computed(() => resolveMods(stats.value))
provide('mods', () => mods.value)

const skills = computed(() => {
  const stat = (skill: string) => skillStats[skill.toLowerCase().replace(/\s+/gi, '')]

  const skills = stats.value.skills || []
  const expertises = stats.value.expertise || []
  const prof = (skill: string) => {
    if (expertises.includes(skill)) return '+prof+prof'
    if (skills.includes(skill)) return '+prof'
    return ''
  }

  const bonus = stats.value.skillBonus ? ensurePrefix(`${stats.value.skillBonus}`, '+') : ''

  const proficiencies = [...new Set([...skills, ...expertises])]
  return proficiencies.map((skill) => ({
    skill,
    dice: `${stat(skill)}${prof(skill)}${bonus}`,
  }))
})

const subtitle = computed(() => {
  const subtype = stats.value.subtype ? ` (${stats.value.subtype})` : ''
  const swarm = stats.value.swarm ? ` swarm of ${stats.value.swarm}` : ''
  const alignment = stats.value.alignment
    ? ensurePrefix(alignments[stats.value.alignment || ''] || stats.value.alignment, ', ')
    : ''
  return titleCase(
    `${stats.value.size}${swarm} ${stats.value.type}${stats.value.swarm ? 's' : ''}${subtype}${alignment}`,
  )
})

const spellcastingData = computed(() => arrayify(stats.value.spellcasting || []))
</script>

<template>
  <div ref="self" class="statblock card secondary">
    <div class="stats">
      <div class="container title-container">
        <h1 class="title">{{ stats.title }}</h1>
        <div class="quick-info container vertical">
          <span v-if="stats.cr">CR&nbsp;{{ stats.cr }}</span>
          <span v-else-if="stats.level">Level&nbsp;{{ stats.level }}</span>
          <span v-if="stats.role">{{ stats.role }}</span>
          <span v-else-if="stats.class">{{ stats.class }}</span>
        </div>
      </div>
      <h2 class="subtitle">{{ subtitle }}</h2>

      <div class="divider" />
      <StatblockAttributes :stats="stats" />
      <AbilityScores v-if="!stats.abilities.hide" :stats="stats" />
      <div v-if="!stats.abilities.hide" class="divider" />
      <Statistic v-if="stats.player" name="Hit Dice">
        <Counter uses="level" lr="reset" name="hd" />
      </Statistic>
      <Statistic v-if="stats.capacity" name="Creature Capacity">
        {{ stats.capacity }}
      </Statistic>
      <Statistic v-if="stats.cargo" name="Cargo Capacity">
        {{ stats.cargo }}
      </Statistic>
      <Statistic v-if="stats.skills" name="Skills">
        <span v-for="skill in skills" :key="skill.skill" class="comma-sep">
          {{ skill.skill }}&nbsp;<Dice :dice="skill.dice" />
        </span>
      </Statistic>
      <Statistic v-if="stats.vulnerabilities" name="Vulnerabilities">
        {{ stats.vulnerabilities }}
      </Statistic>
      <Statistic v-if="stats.resistances" name="Resistances">
        {{ stats.resistances }}
      </Statistic>
      <Statistic v-if="stats.immunities" name="Immunities">
        {{ stats.immunities }}
      </Statistic>
      <Statistic v-if="stats.languages" name="Languages">
        {{ stats.languages }}
      </Statistic>
      <div v-if="stats.player" class="divider" />
      <div v-if="stats.player" class="restbuttons container horizontal">
        <button @click="emitRest('sr')">Short Rest</button>
        <button @click="emitRest('lr')">Long Rest</button>
      </div>
      <div class="divider"></div>
    </div>
    <div class="box-text">
      <Spellcasting v-for="(data, i) in spellcastingData" :key="i" :stats="stats" :data="data" />
      <slot v-bind="mods" />
    </div>
  </div>
</template>

<style scoped lang="postcss">
.character-sheet .statblock {
  column-span: all;

  background: transparent;
  --primary: inherit;
  margin-top: 1em;
  margin-bottom: 0;
}

.statblock {
  column-count: 2;
  column-gap: 2em;
  margin-bottom: 1.5em;
  margin-top: 2em;
  font-size: 16px;

  --background-light: #5b3d37;

  &:first-child {
    margin-top: 0;
  }

  .stats {
    page-break-inside: avoid;

    .title-container {
      justify-content: space-between;
      align-items: center;

      .title {
        font-size: 1.5rem;
        border-bottom: none;
        color: var(--primary);
      }

      .quick-info {
        font-family: var(--font-stack-title);
        color: var(--primary);
        font-weight: 500;
        font-size: 0.75rem;
        line-height: 1;
        text-align: right;
        text-transform: capitalize;
      }
    }

    .subtitle {
      font-size: 0.9rem;
      font-weight: 300;
      border-bottom: none;
    }
  }

  .divider {
    border-top: 4px solid var(--primary);
    margin: 0.75em 0;
  }

  .restbuttons {
    display: flex;
    justify-content: space-around;

    button {
      margin: 0;
    }
  }

  @media screen and (max-width: 600px) {
    padding: 0.5em;
    column-count: 1;
  }
}
</style>

<style lang="postcss">
.statblock {
  .box-text {
    table {
      display: table;
      width: 100%;
    }
    ul {
      padding-left: 0.5em;
    }
  }
}
</style>
